export interface ClientSettings {
  clientName: string;
  clientNameGlobal: string;
  censusReportHour: string;
  showUnassignedFacilities: boolean;
  approvedAdmission: boolean;
  showAiAdmissionOutlook: boolean;
  showMedicareEligibility: boolean;
  useNewPatientInfo: boolean;
  /** IANA zone for this region. SettingsService reads it as the region source. */
  timezone?: string;
  /** IANA zone for the parent client when it differs from the region. */
  clientTimezone?: string;
  uiSettings: {
    // Optional so callers that rebuild uiSettings from a partial spread stay
    // assignable. ClientSettingsService always seeds it from
    // CLIENT_SETTINGS_DEFAULTS and preserves it in mergeSettings, so in
    // practice it is always present.
    newVersionPages?: {
      jobInfo: boolean;
      referralResponseReport: boolean;
    };
    // Per-client product entitlements. Absent, or an absent key, means the product is enabled —
    // the backend does not send this yet.
    // `risk` is the EXCEPTION and is read opt-IN (=== true), not opt-out: RiskAI is a
    // sellable product, so an unconfigured region must not receive it. It is therefore
    // NOT wired to `productFlag`/productEnabled (which is `!== false`); the single
    // reader is isHighRiskEnabledForClient().
    products?: { gateway?: boolean; comply?: boolean; mds?: boolean; risk?: boolean; general?: boolean };
    referralResponse?: Record<string, boolean>;
    externalAccounts?: Record<string, { type: string; displayName?: string; loginTaskType?: string; loginTaskSubType?: string; isEnabled: boolean }>;
    smsEnabled?: boolean;
    aiDecisionLogicEnabled?: boolean;
    enablePccFileDeletion?: boolean;
    enablePccFileMerge?: boolean;
    newNavigation?: boolean;
    internalDeclineReasonsEnabled?: boolean;
    medicationPricingEnabled?: boolean;
    // OPT-IN like `products.risk`: the Discharge Plan is a paid option, switched
    // on per client by the SNF AI admin (177) on Client Settings. Absent means
    // never granted. Readers go through isDischargePlanEnabled(); nothing may
    // coerce the stored tri-state (undefined = never configured) on the way in.
    dischargePlanEnabled?: boolean;
    // Same shape: Scheduled Emails is a paid option too. Readers go through
    // isScheduledEmailsEnabled().
    scheduledEmailsEnabled?: boolean;
    // Same shape: offers the MDS roles (134, 135) on the Users page. Readers go through isMdsRolesEnabled().
    mdsRolesEnabled?: boolean;
    // Opt-in: absent keeps messages facility-scoped. On, every user in the region reads every message.
    messagesVisibleToAllFacilities?: boolean;
    // Opt-in: absent means the legacy Referral Inbox stays on /manageb/dashboard. Set straight in Mongo.
    newReferralInbox?: boolean;
    gatewayReferralLinksDisabled?: boolean;
    timezone?: string;
  };
}

/**
 * The one reading of `uiSettings.dischargePlanEnabled`: only an explicit true
 * is ON. Shared by the admin toggle and by ClientSettingsService when it
 * caches the flag for the synchronous readers in discharge-plan-entitlement.ts.
 */
export function isDischargePlanEnabled(uiSettings: Pick<ClientSettings['uiSettings'], 'dischargePlanEnabled'> | undefined): boolean {
  return uiSettings?.dischargePlanEnabled === true;
}

/** The one reading of `uiSettings.scheduledEmailsEnabled`: only an explicit true is ON. */
export function isScheduledEmailsEnabled(uiSettings: Pick<ClientSettings['uiSettings'], 'scheduledEmailsEnabled'> | undefined): boolean {
  return uiSettings?.scheduledEmailsEnabled === true;
}

/** The one reading of `uiSettings.mdsRolesEnabled`: only an explicit true is ON. */
export function isMdsRolesEnabled(uiSettings: Pick<ClientSettings['uiSettings'], 'mdsRolesEnabled'> | undefined): boolean {
  return uiSettings?.mdsRolesEnabled === true;
}

export function isMessagesVisibleToAllFacilities(uiSettings: Pick<ClientSettings['uiSettings'], 'messagesVisibleToAllFacilities'> | undefined): boolean {
  return uiSettings?.messagesVisibleToAllFacilities === true;
}

export const GATEWAY_REFERRAL_LINKS_DISABLED_KEY = 'nav.gatewayReferralLinksDisabled';

export function isGatewayReferralLinksDisabled(uiSettings: Pick<ClientSettings['uiSettings'], 'gatewayReferralLinksDisabled'> | undefined): boolean {
  return uiSettings?.gatewayReferralLinksDisabled === true;
}

export type ClientSettingsUpdateBody = Omit<ClientSettings, 'clientName' | 'clientNameGlobal'> & {
  // Server rejects a messagesVisibleToAllFacilities write without it (400) and answers 409 when it no longer matches.
  expected?: { uiSettings: { messagesVisibleToAllFacilities: boolean } };
};

export const CLIENT_SETTINGS_DEFAULTS: ClientSettings = {
  clientName: '',
  clientNameGlobal: '',
  censusReportHour: '',
  showUnassignedFacilities: false,
  approvedAdmission: false,
  showAiAdmissionOutlook: true,
  showMedicareEligibility: true,
  useNewPatientInfo: false,
  uiSettings: {
    newVersionPages: { jobInfo: true, referralResponseReport: true },
    newNavigation: false,
    // Always present so [(ngModel)] on the admin screen has an object to bind to.
    // Empty, not `{ risk: false }`: absent means unconfigured, which is what the
    // opt-in read expects.
    products: {},
    timezone: '',
  },
};

export function cloneClientSettings(s: ClientSettings): ClientSettings {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return {
    ...s,
    uiSettings: {
      ...s.uiSettings,
      newVersionPages: { ...s.uiSettings.newVersionPages },
      // Deep-copied for the same reason as newVersionPages: the admin screen
      // binds straight into this object, and a shared reference would mutate
      // CLIENT_SETTINGS_DEFAULTS itself.
      products: { ...s.uiSettings.products },
      timezone: s.uiSettings.timezone,
    },
  } as ClientSettings;
}
